import { TicketCardItem, TicketBrut } from '../../Services/EntiteProjet';
import { formaterDateAvecJour } from '../../Services/format';
import { TicketsResponse } from './tickets.types';
import * as CATEGORIE from './tickets.constants';

export function extraireTickets(response: TicketsResponse): TicketBrut[] {
  return Array.isArray(response?.tickets) ? response.tickets : [];
}

/**
 * Adapte les réponses des listes parc/installation au même modèle de carte.
 * Les synthèses API priment sur les listes de détail, même lorsqu'elles valent null ou 0.
 * undefined signale une donnée indisponible à matérialiser par un cadre violet.
 */
export function adapterTicket(ticket: TicketBrut): TicketCardItem {
  const installation = ticket.installation ?? null;
  const issueBrut = normaliserLibelleHtml(ticket.libelle) ?? 'Ticket sans libellé';
  const nbMessages = lireCompteur(ticket.nbMessages) ?? ticket.messages?.length;

  return {
    id: formaterId(ticket),
    installationId: lireNombre(installation?.id ?? ticket.id_installation),
    site: installation?.nom ?? 'Installation inconnue',
    reference: formaterReference(ticket),
    referenceClass: resoudreClasseReference(lireNombre(ticket.cat)),
    issue: issueBrut,
    issueTooltip: extraireTexteDepuisHtml(issueBrut),
    dateDebut: formaterDateAvecJour(ticket.date_ouverture),
    dateCreation: ticket.date_ouverture,
    dateFin: formaterDateOptionnelle(ticket.date_fermeture),
    priority: formaterPriorite(lireNombre(ticket.niveau)),
    nbMessages,
    dateDernierMessage:
      ticket.date_dernier_message === undefined
        ? extraireDateDernierMessage(ticket.messages)
        : normaliserDate(ticket.date_dernier_message),
    dateInterventionPlanifiee: normaliserDate(ticket.date_intervention_planifiee),
    dateDerniereIntervention:
      ticket.date_derniere_intervention === undefined
        ? extraireDateDerniereIntervention(ticket.bons_intervention)
        : normaliserDate(ticket.date_derniere_intervention),
    nbBonsIntervention: lireCompteur(ticket.nbBonsIntervention) ?? ticket.bons_intervention?.length,
    pertes: resoudrePertes(ticket.presence_perte),
    vu: ticket.messages_non_lus !== true,
    bonsInterventionNonLus: ticket.bons_intervention_non_lus,
  };
}

/** Une absence explicite (null) ne doit pas déclencher de valeur de remplacement. */
function normaliserDate(valeur: string | null | undefined): string | null | undefined {
  if (valeur == null) return valeur;
  return Number.isFinite(new Date(valeur).getTime()) ? valeur : undefined;
}

/** « indeterminé » est un état métier valide, distinct d'un champ API absent. */
function resoudrePertes(presence: TicketBrut['presence_perte']): TicketCardItem['pertes'] {
  switch (presence) {
    case 'oui':
      return 'avérées';
    case 'non':
      return 'aucune';
    case 'possible':
      return 'possibles';
    case 'indeterminé':
      return 'indéterminées';
    default:
      return undefined;
  }
}

/** Repli pour les anciennes réponses sans date_dernier_message, sans modifier la liste. */
function extraireDateDernierMessage(messages?: unknown[]): string | undefined {
  return messages
    ?.flatMap((message) => {
      if (typeof message !== 'object' || message === null || !('date_intervention' in message))
        return [];
      const date = message.date_intervention;
      return typeof date === 'string' && Number.isFinite(new Date(date).getTime()) ? [date] : [];
    })
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
}

/**
 * Repli lorsque date_derniere_intervention est absente : dernière date effective passée.
 * date_deb prime sur la date de saisie du bon ; les dates futures ne sont pas des réalisations.
 * Une liste vide vaut null, une liste non datable reste indisponible (undefined).
 */
function extraireDateDerniereIntervention(bons?: unknown[]): string | null | undefined {
  if (!bons) return undefined;
  if (bons.length === 0) return null;
  const maintenant = Date.now();
  return bons
    .flatMap((bon) => {
      if (typeof bon !== 'object' || bon === null) return [];
      const date =
        ('date_deb' in bon ? bon.date_deb : undefined) ??
        ('date_intervention' in bon ? bon.date_intervention : undefined);
      if (typeof date !== 'string') return [];
      const instant = new Date(date).getTime();
      return Number.isFinite(instant) && instant <= maintenant ? [date] : [];
    })
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
}

/** Un compteur manquant ou invalide ne doit pas être confondu avec un compteur à zéro. */
function lireCompteur(valeur: number | string | undefined): number | undefined {
  const nombre = lireNombre(valeur);
  return nombre !== undefined && Number.isSafeInteger(nombre) && nombre >= 0 ? nombre : undefined;
}

function lireNombre(valeur: number | string | undefined): number | undefined {
  if (typeof valeur === 'number' && Number.isFinite(valeur)) {
    return valeur;
  }
  if (typeof valeur === 'string' && valeur.trim() !== '') {
    const nombre = Number(valeur.trim());
    return Number.isFinite(nombre) ? nombre : undefined;
  }
  return undefined;
}

function formaterReference(ticket: TicketBrut): string {
  const brute =
    ticket.reference ??
    ticket.ref ??
    ticket.code ??
    (ticket.numero != null ? String(ticket.numero) : undefined);

  if (brute) {
    return brute.startsWith('#') ? brute : `#${brute}`;
  }

  const id = ticket.id != null ? String(ticket.id) : 'N/A';
  return `#${id}`;
}

function formaterId(ticket: TicketBrut): string {
  return ticket.id != null ? String(ticket.id) : formaterReference(ticket);
}

function formaterPriorite(niveau?: number): string | undefined {
  return typeof niveau === 'number' ? `Priorite ${niveau}` : undefined;
}

function formaterDateOptionnelle(valeur?: string): string | undefined {
  return valeur ? formaterDateAvecJour(valeur) : undefined;
}

function resoudreClasseReference(cat?: number): string {
  switch (cat) {
    case CATEGORIE.DEFAUT_PRODUCTION:
    case CATEGORIE.PERTE_PRODUCTION:
    case CATEGORIE.ARRET_PRODUCTION_ONDULEUR:
    case CATEGORIE.PERTE_PRODUCTION_ONDULEUR:
      return 'ticket-card-ref-alert';

    case CATEGORIE.PROBLEME_COM_OU_PROD:
    case CATEGORIE.DONNEES_MANQUANTES:
    case CATEGORIE.DONNEES_ERRONNEES:
    case CATEGORIE.RESEAU_HORS_LIMITES:
      return 'ticket-card-ref-warning';

    case CATEGORIE.PROBLEME_COM:
    case CATEGORIE.ALARMES_NON_CONCLUANTES:
      return 'ticket-card-ref-caution';

    case CATEGORIE.ARRET_TELESUIVI:
    case CATEGORIE.MISE_EN_SERVICE_TELESUIVI:
    case CATEGORIE.REGLAGE_PARAMETRE:
      return 'ticket-card-ref-info';

    case CATEGORIE.INTERVENTION_PREVENTIVE:
    default:
      return '';
  }
}

function normaliserLibelleHtml(valeur?: string): string | undefined {
  if (!valeur) return undefined;
  const htmlNormalise = valeur
    .replace(/\r?\n/g, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/<\/(b|strong)>\s*:/gi, '</$1>:')
    .trim();
  return htmlNormalise || undefined;
}

function extraireTexteDepuisHtml(valeur?: string): string | undefined {
  if (!valeur) return undefined;
  const texte = valeur
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return texte || undefined;
}
